 
import React, { useEffect, useRef } from 'react';
import { NSTable } from 'bricks';
import classNames from 'classnames';
import RenderIf from 'components/RenderIf/RenderIf';
import { useCustomScroll } from 'views/ProFormaTable/hooks/useCustomScroll';
import { ProFormaModelIds } from 'constants/proForma';
import PeriodCellValue from '../../../ProFormaDevelopment/DevelopmentUsesTable/PeriodCellValue/PeriodCellValue';

export interface IMonthlyAnalysisPeriod {
    periodName: string;
    periodDate: string;
    leasedPercentage: string | null;
    economicOccupancyPercentage: string | null;
}

export interface IMonthlyAnalysisTableProps {
    proFormaModelId: ProFormaModelIds;
    monthlyAnalysisPeriods: IMonthlyAnalysisPeriod[];
    operationBeginMonth?: number;
}

const MonthlyAnalysisTable = ({ proFormaModelId, monthlyAnalysisPeriods, operationBeginMonth }: IMonthlyAnalysisTableProps) => {
    const operationStartRef = useRef<HTMLTableCellElement>(null);
    const {
        tableRef,
        handleTableScroll,
        scrollToElement,
    } = useCustomScroll();

    useEffect(() => {
        if (operationStartRef.current && operationBeginMonth) {
            scrollToElement(operationStartRef.current);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operationBeginMonth]);

    return (
        <div
            className={classNames(['mt-2', {
                'mr-n3': operationBeginMonth,
            }])}
        >
            <div
                className="d-flex scrollable-container pb-1"
                ref={tableRef}
                onScroll={() => handleTableScroll('table')}
            >
                <NSTable
                    className="NSTable--no-footer NSTable--sticky-headers NSTable--sticky-headers--full-width NSTable--sticky-headers--sticky-first-column"
                >
                    <thead className="NSTable__thead">
                        <tr className="NSTable__thead__tr">
                            <th className="NSTable__thead__tr__th  NSTable__thead__tr__th--width-140 align-middle py-1">
                                Monthly analysis
                            </th>
                            {monthlyAnalysisPeriods.map((period, index) => (
                                <th
                                    key={`monthly-analysis-header-${period.periodName}`}
                                    className="NSTable__thead__tr__th align-middle NSTable__thead__tr__th--grey-bg py-1"
                                    id={`monthly-analysis-header-${index}`}
                                    ref={operationBeginMonth && operationBeginMonth - 1 === index ? operationStartRef : undefined}
                                >
                                    <RenderIf isTrue={!period.periodDate}>
                                        <div className="h6 NSTable__thead__tr__th--grey-text
                                            NSTable__thead__tr__th--opacity-3 text-uppercase text-nowrap text-secondary text-right mb-0"
                                        >
                                            Month #
                                        </div>
                                        <div className="h6 text-uppercase text-right text-nowrap NSTable__thead__tr__th--opacity-3 mt-1">
                                            {period.periodName}
                                        </div>
                                    </RenderIf>
                                    <RenderIf isTrue={period.periodDate}>
                                        <div className="h6 NSTable__thead__tr__th--grey-text text-uppercase text-nowrap text-secondary text-right mb-0">
                                            Month
                                            {' '}
                                            {index}
                                        </div>
                                        <div className="h6 text-uppercase text-right text-nowrap mt-1">{period.periodName}</div>
                                    </RenderIf>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody className="NSTable__tbody">
                        <tr className="NSTable__tbody__tr">
                            <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle
                        NSTable__tbody__tr__td__input text-dark"
                            >
                                Leased %
                            </td>
                            {monthlyAnalysisPeriods.map((period, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <React.Fragment key={`monthly-analysis-leased-cell-${index}`}>
                                    <RenderIf isTrue={!period.periodDate}>
                                        <PeriodCellValue
                                            // eslint-disable-next-line react/no-array-index-key
                                            key={`monthly-analysis-period-header-${index}`}
                                            tooltipId={`monthly-analysis-period-header-${index}`}
                                            period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                            value="—"
                                            cellClassName="d-flex NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle
                        NSTable__tbody__tr__td__input text-dark text-right"
                                        />
                                    </RenderIf>
                                    <RenderIf isTrue={period.periodDate}>
                                        <td key={`leased-${period.periodName}`} className="text-dark text-right">
                                            {period.leasedPercentage}
                                        </td>
                                    </RenderIf>
                                </React.Fragment>
                            ))}
                        </tr>
                        <RenderIf isTrue={[ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proFormaModelId)}>
                            <tr className="NSTable__tbody__tr">
                                <td className="NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle
                                               NSTable__tbody__tr__td__input text-dark"
                                >
                                    Economic occ. %
                                </td>
                                {monthlyAnalysisPeriods.map((period, index) => (
                                    <React.Fragment key={`monthly-analysis-economic-cell-${period.periodName}`}>
                                        <RenderIf isTrue={!period.periodDate}>
                                            <PeriodCellValue
                                                // eslint-disable-next-line react/no-array-index-key
                                                key={`monthly-analysis-period-cell-${index}`}
                                                tooltipId={`monthly-analysis-period-cell-${index}`}
                                                period={{ label: 'MON YEAR', value: '', isPlaceholder: true }}
                                                value="—"
                                                cellClassName="d-flex NSTable__tbody__tr__td NSTable__tbody__tr__td--right-border align-middle
                        NSTable__tbody__tr__td__input text-dark text-right"
                                            />
                                        </RenderIf>
                                        <RenderIf isTrue={period.periodDate}>

                                            <td key={`economic-occ-${period.periodName}`} className="text-dark text-right">
                                                {period.economicOccupancyPercentage}
                                            </td>
                                        </RenderIf>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </RenderIf>
                    </tbody>
                </NSTable>
            </div>
        </div>
    );
};

export default MonthlyAnalysisTable;
